import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiArrowUpRight } from "react-icons/fi";

const MotionLink = motion(Link);

/**
 * Represents a button component with hover and tap animations.
 * Displays a name and an icon. Renders a router link when `to` is passed,
 * an anchor when `href` is passed, and a <button> otherwise.
 *
 * @component
 * @param {string} name - The text to display on the button.
 * @param {string} color - The background color of the button.
 * @param {string} to - Internal route to navigate to.
 * @param {string} href - External URL or file to open.
 * @param {boolean} download - Download the `href` file instead of opening it.
 * @param {string} variant - "ghost" for an outlined button.
 * @param {ReactNode} icon - Icon to show instead of the arrow.
 */

const Button = ({ name, color, to, href, download, variant, icon, disabled }) => {
  const props = {
    className: `btn${variant ? ` btn-${variant}` : ""}`,
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.99 },
    style: color ? { backgroundColor: color } : undefined,
  };

  const content = (
    <>
      <p>{name}</p>
      <div>{icon || <FiArrowUpRight className="arrow-icon" aria-hidden="true" />}</div>
    </>
  );

  if (to) {
    return (
      <MotionLink to={to} {...props}>
        {content}
      </MotionLink>
    );
  }

  if (href) {
    const external = /^https?:\/\//.test(href);
    return (
      <motion.a
        href={href}
        download={download || undefined}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        {...props}
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button type="submit" disabled={disabled} {...props}>
      {content}
    </motion.button>
  );
};

export default Button;
